// ✅ src/pages/VehicleV2/VehicleSubTypeMaster.jsx (FULL FILE)
// UI NOT CHANGED ✅
// VehicleTypeId comes from URL query: ?vehicletypeid=2081
//
// Backend mount:
//   app.use("/api/vehicleSubTypeMaster", vehicleSubTypeMaster);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/vehicleSubTypeMaster/vehicle-types
//   GET  https://nissi-india.com/api/vehicleSubTypeMaster/list?vehicleTypeId=..
//   POST https://nissi-india.com/api/vehicleSubTypeMaster/save
//   POST https://nissi-india.com/api/vehicleSubTypeMaster/delete

import HeaderBar from "../../components/HeaderBar";
import { useEffect, useMemo, useState, useContext } from "react";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaTrash, FaEdit } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/equipmentTypeMaster";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v) => String(v ?? "").trim();

const VehicleSubTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ READ FROM URL
  const [searchParams] = useSearchParams();
  const urlVehicleTypeId = toInt(searchParams.get("vehicletypeid"), 0);

  // ✅ keep vehicle type as string for select display
  const [vehicleTypeId, setVehicleTypeId] = useState(
    urlVehicleTypeId ? String(urlVehicleTypeId) : ""
  );

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);

  const [formData, setFormData] = useState({
    vehicleSubType: "",
  });

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Map for display
  const vehicleTypeNameById = useMemo(() => {
    const m = new Map();
    vehicleTypes.forEach((v) => m.set(String(v.id), v.name));
    return m;
  }, [vehicleTypes]);

  const selectedVehicleTypeName =
    vehicleTypeNameById.get(String(vehicleTypeId)) || "";

  // keep id in sync if url changes
  useEffect(() => {
    if (urlVehicleTypeId) setVehicleTypeId(String(urlVehicleTypeId));
  }, [urlVehicleTypeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const fetchVehicleTypes = async () => {
    try {
      const r = await axios.get(`${API_BASE}/vehicle-types`, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Failed to load vehicle types");
        return;
      }

      const types = Array.isArray(r.data?.data) ? r.data.data : [];
      const normalizedTypes = types.map((x) => ({
        id:
          x?.id ??
          x?.vehicleTypeId ??
          x?.vehicletypeid ??
          x?.fld_vehicletypeid ??
          x?.fld_vehicleTypeId ??
          0,
        name:
          x?.name ??
          x?.vehicleType ??
          x?.vehicletypename ??
          x?.fld_vehicletypename ??
          x?.["Vehicle Type"] ??
          "",
      }));

      setVehicleTypes(normalizedTypes);

      // ✅ if URL id is missing, fallback first item
      if (!urlVehicleTypeId && !vehicleTypeId && normalizedTypes.length) {
        setVehicleTypeId(String(normalizedTypes[0].id));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vehicle types");
    }
  };

  const fetchSubTypes = async (vehTypeIdStr) => {
    const id = toInt(vehTypeIdStr, 0);
    if (!id) {
      setSubTypes([]);
      return;
    }

    try {
      const r = await axios.get(`${API_BASE}/list`, {
        params: { vehicleTypeId: id },
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Failed to load sub types");
        setSubTypes([]);
        return;
      }

      const rows = (r.data?.data || []).map((x) => ({
        id:
          x?.id ??
          x?.subTypeId ??
          x?.vehicleSubTypeId ??
          x?.vehiclesubtypeid ??
          x?.fld_vehiclesubtypeid ??
          0,
        vehicleTypeId: String(id),
        vehicleSubType:
          x?.vehicleSubType ??
          x?.vehicle_subtype ??
          x?.fld_vehicle_subtypename ??
          x?.vehiclesubtypename ??
          "",
      }));

      setSubTypes(rows);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load sub types");
      setSubTypes([]);
    }
  };

  useEffect(() => {
    fetchVehicleTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  // ✅ load grid when vehicleTypeId is known
  useEffect(() => {
    if (!vehicleTypeId) return;

    // IMPORTANT: If URL id exists, do not allow changing vehicleTypeId from elsewhere
    if (urlVehicleTypeId && toInt(vehicleTypeId) !== urlVehicleTypeId) {
      setVehicleTypeId(String(urlVehicleTypeId));
      return;
    }

    fetchSubTypes(vehicleTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeId, urlVehicleTypeId, commonHeaders]);

  const handleSave = async () => {
    if (!toStr(vehicleTypeId) || !toStr(formData.vehicleSubType)) {
      toast.error("Please fill in all fields!");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        subTypeId: editingId ? toInt(editingId) : 0,
        vehicleTypeId: toInt(vehicleTypeId),
        vehicleSubType: toStr(formData.vehicleSubType),
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Failed to save vehicle sub type!");
        return;
      }

      toast.success(
        r.data?.message ||
          (editingId ? "Updated Successfully" : "Saved Successfully")
      );

      setEditingId(null);
      setFormData({ vehicleSubType: "" });

      await fetchSubTypes(vehicleTypeId);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save vehicle sub type!");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ vehicleSubType: item.vehicleSubType });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub type?")) return;

    try {
      const r = await axios.post(
        `${API_BASE}/delete`,
        { ids: [toInt(id)] },
        { headers: commonHeaders }
      );

      if (!r.data?.success) {
        toast.error(r.data?.message || "Delete failed");
        return;
      }

      toast.success(r.data?.message || "Deleted Successfully");

      if (editingId === id) {
        setEditingId(null);
        setFormData({ vehicleSubType: "" });
      }

      await fetchSubTypes(vehicleTypeId);
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ vehicleSubType: "" });
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE SUB TYPE</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Header Section */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Sub Types
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {subTypes.length} Records
            </span>
          </div>

          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? "border-gray-600 text-white hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Listing
          </button>
        </div>

        {/* Form Container */}
        <div
          className={`rounded-xl p-6 border mb-6 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* ✅ Vehicle Type Field (READ ONLY) */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Vehicle Type
              </label>
              <select
                value={String(vehicleTypeId || "")}
                disabled
                className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value={String(vehicleTypeId || "")}>
                  {selectedVehicleTypeName || (vehicleTypeId ? "Loading..." : "No Vehicle Type")}
                </option>
              </select>
            </div>

            {/* Vehicle Sub Type Field */}
            <div>
              <label
                htmlFor="vehicleSubType"
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Vehicle Sub Type
              </label>
              <input
                id="vehicleSubType"
                name="vehicleSubType"
                type="text"
                value={formData.vehicleSubType}
                onChange={handleInputChange}
                placeholder="Enter vehicle sub type..."
                className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={editingId ? handleCancelEdit : () => setFormData({ vehicleSubType: "" })}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {editingId ? "Cancel Edit" : "Clear"}
            </button>

            <button
              onClick={handleSave}
              disabled={loading || !toStr(vehicleTypeId) || !toStr(formData.vehicleSubType)}
              className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                loading || !toStr(vehicleTypeId) || !toStr(formData.vehicleSubType)
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {editingId ? "Updating..." : "Saving..."}
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  {editingId ? "Update" : "Save"} Vehicle Sub Type
                </>
              )}
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold">S.No</th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Type</th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Sub Type</th>
                  <th className="px-4 py-4 text-center font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {subTypes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-500">
                      No vehicle sub types found.
                    </td>
                  </tr>
                ) : (
                  subTypes.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                        isDark ? "bg-gray-900" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {index + 1}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {selectedVehicleTypeName}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {item.vehicleSubType}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200"
                          >
                            <FaEdit className="w-3 h-3" />
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(item.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transform hover:-translate-y-0.5 transition-all duration-200"
                          >
                            <FaTrash className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSubTypeMaster;